import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";

import { useEffect, useState } from "react";

import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { Toaster } from "@/components/ui/sonner";

import Economicos from "./pages/Economicos";
import Locales from "@/pages/Locales";
import Casas from "@/pages/Casas";
import Terrenos from "@/pages/Terrenos";
import Bodegas from "@/pages/Bodegas";
import Buscar from "@/pages/Buscar";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import Registro from "@/pages/Registro";
import CrearCuenta from "@/pages/CrearCuenta";
import PublicarGratis from "@/pages/PublicarGratis";
import IniciarSesion from "@/pages/IniciarSesion";
import RecuperarContrasena from "@/pages/RecuperarContrasena";
import CambiarContrasena from "@/pages/CambiarContrasena";
import MisPropiedades from "@/pages/MisPropiedades";
import EditarPropiedad from "@/pages/EditarPropiedad";
import CompletarPerfil from "@/pages/CompletarPerfil";
import MiPerfil from "@/pages/MiPerfil";
import EditarDatos from "@/pages/EditarDatos";
import AvisoPrivacidad from "@/pages/AvisoPrivacidad";
import TerminosCondiciones from "@/pages/TerminosCondiciones";
import QuienesSomos from "@/pages/QuienesSomos";
import ComoTeAyudamos from "@/pages/ComoTeAyudamos";
import ConQuienesColaboramos from "@/pages/ConQuienesColaboramos";
import PlanProCheckout from "@/pages/PlanProCheckout";
import { ArrowRight, Check, Heart, MapPin, Rocket } from "lucide-react";
import { properties, oportunidadesUnicas } from "@/data/properties";
import { commercialRealEstateAllNews } from "@/data/commercialRealEstateAllNews";
import demoAgentVideo from "@/assets/videos/demo-agente-canva-crop.mp4";
import demoAgentVideoPoster from "@/assets/videos/demo-agente-canva-poster.jpg";
import OficinasConsultorios from "@/pages/OficinasConsultorios";
import AdminLikes from "@/pages/AdminLikes";
import AdminProAlertas from "@/pages/AdminProAlertas";
import AdminPropiedades from "@/pages/AdminPropiedades";
import AdminAgenteWhatsApp from "@/pages/AdminAgenteWhatsApp";
import AdminAgenteConversaciones from "@/pages/AdminAgenteConversaciones";
import CommercialRealEstateNewsPage from "@/pages/CommercialRealEstateNewsPage";
import CommercialRealEstateNewsIndexPage from "@/pages/CommercialRealEstateNewsIndexPage";
import { getAuthSession, getValidAuthSession, saveSessionFromUrl } from "@/lib/supabaseAuth";
import HeartBurst from "@/components/HeartBurst";
import { usePublishedProperties } from "@/hooks/usePublishedProperties";
import {
  getPropertyLikeCount,
  hasLikedProperty,
  savePropertyLike,
} from "@/utils/propertyLikes";

const SHOW_PUBLIC_PLANS = true;

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

const getPropertyIdFromLink = (link = "") => {
  const match = String(link).match(/\/propiedad\/(\d+)/);
  return match ? match[1] : "";
};

const normalizeOpportunityText = (text = "") =>
  String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isCoyoacanProperty = (property = {}) => {
  const text = [
    property.title,
    property.address,
    property.location,
    property.city,
    property.ciudad,
    property.municipio,
    property.alcaldia,
    property.state,
    property.entidad,
    property.zona,
    property.zonaDetalle,
    property.description,
  ]
    .filter(Boolean)
    .join(" ");

  const normalized = normalizeOpportunityText(text);
  return normalized.includes("coyoacan") || normalized.includes("auriga");
};

const getOpportunityImage = (property = {}) => {
  const media = (property.images || []).find((item) => {
    if (typeof item === "string") return true;
    if (item?.type === "video") return false;
    return item?.src || item?.url;
  });

  if (typeof media === "string") return media;
  return media?.src || media?.url || "";
};

const formatOpportunityRent = (price) => {
  const amount = Number(price || 0);
  if (!amount) return "Renta disponible";
  return `Renta $${amount.toLocaleString("es-MX")}`;
};

const mapPropertyToOpportunity = (property) => ({
  id: property.id,
  title: isCoyoacanProperty(property)
    ? "Oficina en renta excelente ubicacion dentro de plaza"
    : property.title || "Local en Coyoacan",
  subtitle: formatOpportunityRent(property.price),
  promo: "Recién agregado",
  image: getOpportunityImage(property),
  link: `/propiedad/${property.id}`,
  property,
});

const cleanOpportunityLocationValue = (value = "") =>
  String(value || "")
    .replace(/^colonia\s*:?\s*/i, "")
    .replace(/^calle\s*:?\s*/i, "")
    .replace(/^blvd\s*:?\s*/i, "Blvd ")
    .replace(/^av\s*:?\s*/i, "Av ")
    .replace(/\s+/g, " ")
    .trim();

const getOpportunityProperty = (item = {}) => {
  if (item.property) return item.property;

  const propertyId = getPropertyIdFromLink(item.link);
  return properties.find((candidate) => String(candidate.id) === String(propertyId));
};

const getOpportunityCity = (property = {}) => {
  const explicitCity =
    property.city ||
    property.ciudad ||
    property.municipio ||
    property.alcaldia ||
    "";
  const explicitState = property.state || property.entidad || "";
  const text = normalizeOpportunityText(
    [
      explicitCity,
      explicitState,
      property.address,
      property.location,
      property.zona,
      property.description,
    ]
      .filter(Boolean)
      .join(" ")
  );

  if (text.includes("coyoacan") || text.includes("auriga")) {
    return "Coyoacán, Ciudad de México";
  }

  if (explicitCity || explicitState) {
    return [explicitCity, explicitState].filter(Boolean).join(", ");
  }

  return "Culiacán, Sinaloa";
};

function OpportunityLikeButton({ item }) {
  const propertyId = getPropertyIdFromLink(item.link);
  const property =
    properties.find((candidate) => String(candidate.id) === String(propertyId)) || {
      id: propertyId || item.id,
      title: item.title,
      location: item.subtitle || "",
    };

  const [isFavorite, setIsFavorite] = useState(false);
  const [isBursting, setIsBursting] = useState(false);
  const [likeCount, setLikeCount] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setIsFavorite(false);

    getPropertyLikeCount(property.id).then((count) => {
      if (isMounted) setLikeCount(count);
    });

    return () => {
      isMounted = false;
    };
  }, [property.id]);

  const handleClick = async (event) => {
    event.stopPropagation();
    if (hasLikedProperty(property.id)) return;

    setIsBursting(false);
    window.setTimeout(() => setIsBursting(true), 0);
    window.setTimeout(() => setIsBursting(false), 800);
    setIsFavorite(true);
    window.setTimeout(() => setIsFavorite(false), 650);
    setLikeCount((count) => (Number.isFinite(count) ? count + 1 : 1));

    const result = await savePropertyLike(property);
    if (Number.isFinite(result?.count)) {
      setLikeCount(result.count);
    }
  };

  return (
    <button
      onClick={handleClick}
      aria-label={isFavorite ? "Propiedad marcada como favorita" : "Marcar propiedad como favorita"}
      title={isFavorite ? "Ya marcaste esta propiedad" : "Me gusta"}
      className={`absolute right-2 top-2 z-30 flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-black shadow-sm transition ${
        isFavorite
          ? "border-red-100 bg-white text-red-600"
          : "border-white/80 bg-white/95 text-slate-500 hover:text-red-500"
      }`}
    >
      <HeartBurst active={isBursting} />
      <Heart className="h-3.5 w-3.5" fill={isFavorite ? "currentColor" : "none"} />
    </button>
  );
}

function CommercialRealEstateNewsSection() {
  const navigate = useNavigate();

  return (
    <section id="noticias" className="scroll-mt-6 bg-white px-4 pb-10 pt-2">
      <div className="mx-auto max-w-6xl">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-[#0056D2]">
              Tulocalmx te informa
            </p>
            <h2 className="mt-1 text-2xl font-black text-slate-950 sm:text-3xl">
              Noticias que mueven el mercado
            </h2>
          </div>
          <p className="max-w-xl text-sm font-semibold leading-6 text-slate-600">
            IA, retail, rentas y señales de demanda para tomar mejores decisiones comerciales.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {commercialRealEstateAllNews.slice(0, 4).map((item) => (
            <button
              key={item.slug}
              type="button"
              onClick={() => navigate(`/noticias/${item.slug}`)}
              className="group overflow-hidden rounded-md border border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-md"
            >
              <div className={`relative h-28 bg-gradient-to-r ${item.accent}`}>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.28),transparent_28%),linear-gradient(135deg,rgba(255,255,255,0.16),transparent_45%)]" />
                <div className="absolute left-4 top-4 rounded-sm border border-white/40 bg-white/95 px-2 py-1 text-[10px] font-black uppercase text-slate-900 shadow-sm">
                  {item.tag}
                </div>
              </div>

              <div className="p-4">
                <h3 className="line-clamp-2 text-base font-black leading-snug text-slate-950">
                  {item.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm font-semibold leading-6 text-slate-600">
                  {item.summary}
                </p>
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                  <span className="text-xs font-bold text-slate-500">Fuente: {item.sourceName}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-black uppercase text-[#F97316]">
                    Leer nota
                    <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/noticias")}
            className="inline-flex items-center gap-2 rounded-lg border border-orange-200 bg-white px-5 py-3 text-sm font-black uppercase text-[#F97316] shadow-sm transition hover:border-orange-300 hover:bg-orange-50"
          >
            + noticias
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

function Home() {
  const navigate = useNavigate();
  const publishedProperties = usePublishedProperties();
  const coyoacanOpportunity = publishedProperties.find(isCoyoacanProperty);
  const smartOpportunities = oportunidadesUnicas.map((item, index) =>
    index === 2 && coyoacanOpportunity ? mapPropertyToOpportunity(coyoacanOpportunity) : item
  );
  const openOpportunity = (item) => {
    navigate(item.link, item.property ? { state: { publishedProperty: item.property } } : undefined);
  };
  const openPublisherSignup = (plan = "gratis") => {
    const normalizedPlan = plan === "pro" ? "pro" : "gratis";
    if (getAuthSession()?.access_token) {
      navigate(`/registro?plan=${normalizedPlan}`);
      return;
    }

    navigate(`/publicar-gratis?plan=${normalizedPlan}`);
  };
  useEffect(() => {
    if (!["#planes-publicacion", "#noticias"].includes(window.location.hash)) return;
    window.setTimeout(() => {
      document.getElementById(window.location.hash.slice(1))?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  }, []);
  return (
    <div>
      <Header />
      <Hero />

      <section className="bg-white px-4 py-8">
        <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-[0.9fr_1.35fr] md:items-stretch">
          <div className="flex min-h-[250px] flex-col justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600 ring-1 ring-orange-100">
                <Rocket className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-wide text-orange-600">
                  Demo de agentes
                </p>
                <h2 className="mt-1 text-lg font-black leading-tight text-slate-950 sm:text-xl">
                  Evalua una ubicacion comercial con IA
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Mira como el agente ordena zona, competencia y potencial del
                  espacio para un giro especifico.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate("/propiedad/26?demo=agentes")}
              className="mt-5 inline-flex w-fit shrink-0 items-center justify-center rounded-lg border border-orange-500 bg-white px-5 py-3 text-sm font-black text-orange-600 shadow-sm transition hover:bg-orange-50"
            >
              Ver demo de agentes
            </button>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
            <video
              className="h-full min-h-[250px] w-full bg-white object-cover"
              src={demoAgentVideo}
              poster={demoAgentVideoPoster}
              controls
              preload="metadata"
              playsInline
            />
          </div>
        </div>
      </section>

      {/* ================= ELIGE INTELIGENTE ================= */}
      <div className="relative w-full mt-0 py-8">
        <h2 className="text-center text-2xl font-black leading-tight text-slate-950 sm:text-3xl">
          Tu idea necesita el lugar correcto
        </h2>

        <p className="mx-auto mt-2 max-w-2xl text-center text-sm font-semibold leading-6 text-slate-600">
          La IA te ayuda a encontrar el espacio donde puede hacerse realidad.
        </p>

        <div className="mt-5 grid grid-cols-2 gap-4 px-4 sm:grid-cols-3 lg:grid-cols-5">
          {smartOpportunities.map((item) => {
            const opportunityProperty = getOpportunityProperty(item);
            const city = getOpportunityCity(opportunityProperty);
            const neighborhood = cleanOpportunityLocationValue(opportunityProperty?.address);
            const street = cleanOpportunityLocationValue(opportunityProperty?.location);

            return (
              <div
                key={item.id}
                className="group relative flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-1 hover:border-orange-200 hover:shadow-md"
                onClick={() => openOpportunity(item)}
              >
                <div className="relative overflow-hidden bg-slate-100">
                  <OpportunityLikeButton item={item} />
                  {item.status && (
                    <div className="absolute right-2 top-10 z-20 rounded-full bg-white/95 px-2 py-0.5 text-[8px] font-black uppercase text-orange-600 shadow-sm">
                      {item.status}
                    </div>
                  )}
                  {item.promo && (
                    <div className="absolute bottom-2 left-2 right-2 z-20 rounded-sm border border-orange-200 bg-white/95 px-1.5 py-0.5 text-center shadow-sm">
                      <span className="block whitespace-nowrap text-[7px] font-black uppercase leading-4 text-orange-600 sm:text-[8px]">
                        {item.promo}
                      </span>
                    </div>
                  )}

                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-44 w-full object-cover transition duration-200 group-hover:scale-105"
                  />
                </div>

                <div className="flex flex-1 flex-col p-2.5">
                  <h3 className="line-clamp-2 min-h-[34px] text-[11px] font-black uppercase leading-tight tracking-wide text-slate-950 sm:text-[12px]">
                    {item.title}
                  </h3>

                  <p className="mt-1 whitespace-nowrap text-sm font-black leading-none text-[#F97316]">
                    {item.subtitle}
                  </p>

                  {(city || neighborhood || street) && (
                    <div className="mt-2 flex min-h-[92px] items-start gap-1.5 border-t border-slate-100 pt-2 text-[10px] font-semibold leading-tight text-slate-800 sm:text-[10.5px]">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0056D2]" />
                      <div className="min-w-0 space-y-1">
                        {city && (
                          <p className="line-clamp-1 break-words">
                            <span className="font-black text-slate-950">Ciudad:</span>{" "}
                            {city}
                          </p>
                        )}
                        {neighborhood && (
                          <p className="line-clamp-1 break-words">
                            <span className="font-black text-slate-950">Colonia:</span>{" "}
                            {neighborhood}
                          </p>
                        )}
                        {street && (
                          <p className="line-clamp-2 break-words">
                            <span className="font-black text-slate-950">Calle:</span>{" "}
                            {street}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <CommercialRealEstateNewsSection />

      {SHOW_PUBLIC_PLANS && (
      <section id="planes-publicacion" className="bg-slate-50 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Publica tu propiedad
            </p>
            <h2 className="mt-2 text-2xl font-black leading-tight text-slate-950 sm:text-3xl">
              Elige el plan para anunciar tu espacio
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Publica gratis y conoce los beneficios Pro disponibles en promocion de lanzamiento.
            </p>
          </div>

          <div className="mx-auto mt-6 grid max-w-4xl gap-4 md:grid-cols-2">
            {[
              {
                id: "gratis",
                title: "Plan gratis",
                price: "$0",
                description: "Para publicar tu propiedad y recibir contacto directo.",
                features: [
                  "Publicación gratis",
                  "Contacto directo a tu WhatsApp",
                  "Fotos y video de la propiedad",
                  "Ubicación exacta en mapa",
                ],
              },
              {
                id: "pro",
                title: "Plan Pro",
                price: "$0",
                freeLabel: "Gratis",
                period: "Primer mes",
                promo: "Primeras 100 propiedades",
                description: "Publica con más datos, análisis y apoyo de la IA para rentar más rápido y por más tiempo.",
                features: [
                  "Todo lo del plan gratis",
                  "Análisis de zona (IA)",
                  "Asesor (IA)",
                  "Leads perfilados directo a tu WhatsApp",
                  "Promoción de lanzamiento por 1 mes",
                ],
              },
            
            ].map((plan) => (
              <article
                key={plan.id}
                className={`flex h-full flex-col rounded-lg bg-white p-5 shadow-sm ${
                  plan.id === "pro"
                    ? "border-2 border-[#F97316] ring-4 ring-orange-100"
                    : "border border-slate-200"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-black text-slate-950">
                      {plan.title}
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      {plan.description}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    {plan.promo && (
                      <p className="mb-2 inline-flex rounded-md bg-[#F97316] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-sm">
                        {plan.promo}
                      </p>
                    )}
                    {plan.freeLabel ? (
                      <>
                        <p className="whitespace-nowrap text-sm font-black text-slate-400 line-through">
                          {plan.price}
                        </p>
                        <p className="whitespace-nowrap text-2xl font-black text-[#F97316]">
                          {plan.freeLabel}
                        </p>
                      </>
                    ) : (
                      <p className="whitespace-nowrap text-xl font-black text-slate-950">
                        {plan.price}
                      </p>
                    )}
                    <p className="mt-0.5 whitespace-nowrap text-xs font-semibold text-slate-500">
                      {plan.period || "1 publicación · 1 mes"}
                    </p>
                  </div>
                </div>

                <div className="mt-5 flex-1 space-y-2">
                  {plan.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[#0056D2]">
                        <Check size={14} strokeWidth={3} />
                      </span>
                      <span className="text-sm leading-5 text-slate-700">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (plan.id === "pro") {
                      openPublisherSignup("pro");
                      return;
                    }

                    openPublisherSignup(plan.id);
                  }}
                  className={`mt-5 rounded-lg px-4 py-3 text-sm font-black transition ${
                    plan.id === "pro"
                      ? "bg-[#F97316] text-white hover:bg-orange-600"
                      : "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  {plan.id === "pro" ? "Publicar y activar Pro gratis" : "Publicar gratis"}
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>
      )}
      <Footer />
      <FloatingWhatsApp />
      <Toaster />
    </div>
  );
}

function App() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!window.location.hash.includes("access_token=")) return;

    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;

    saveSessionFromUrl(true)
      .then((session) => {
        if (session?.access_token) {
          if (currentPath === "/completar-perfil" || currentPath === "/cambiar-contrasena") {
            navigate(`${currentPath}${currentSearch}`, { replace: true });
            return;
          }

          navigate("/completar-perfil?next=/", { replace: true });
        }
      })
      .catch(() => {
        navigate("/iniciar-sesion", { replace: true });
      });
  }, [navigate]);

  useEffect(() => {
    getValidAuthSession();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("panel") === "agente") {
      navigate("/admin/agente-whatsapp", { replace: true });
    }
  }, [navigate]);

  return (
    <>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/economicos" element={<Economicos />} />
      <Route path="/locales" element={<Locales />} />
      <Route path="/casas" element={<Casas />} />
      <Route path="/terrenos" element={<Terrenos />} />
      <Route path="/bodegas" element={<Bodegas />} />
      <Route path="/buscar" element={<Buscar />} />

      <Route path="/propiedad/:id" element={<PropertyDetailPage />} />
      <Route path="/noticias" element={<CommercialRealEstateNewsIndexPage />} />
      <Route path="/noticias/:slug" element={<CommercialRealEstateNewsPage />} />
      <Route path="/registro" element={<Registro />} />
      <Route path="/crear-cuenta" element={<CrearCuenta />} />
      <Route path="/publicar-gratis" element={<PublicarGratis />} />
      <Route path="/iniciar-sesion" element={<IniciarSesion />} />
      <Route path="/recuperar-contrasena" element={<RecuperarContrasena />} />
      <Route path="/cambiar-contrasena" element={<CambiarContrasena />} />
      <Route path="/completar-perfil" element={<CompletarPerfil />} />
      <Route path="/mi-perfil" element={<MiPerfil />} />
      <Route path="/editar-datos" element={<EditarDatos />} />
      <Route path="/mis-propiedades" element={<MisPropiedades />} />
      <Route path="/editar-propiedad/:id" element={<EditarPropiedad />} />
      <Route path="/privacidad" element={<AvisoPrivacidad />} />
      <Route path="/terminos" element={<TerminosCondiciones />} />
      <Route path="/quienes-somos" element={<QuienesSomos />} />
      <Route path="/como-te-ayudamos" element={<ComoTeAyudamos />} />
      <Route path="/con-quienes-colaboramos" element={<ConQuienesColaboramos />} />
      <Route path="/plan-pro" element={<PlanProCheckout />} />
      <Route path="/oficinas" element={<OficinasConsultorios />} />
      <Route path="/admin/likes" element={<AdminLikes />} />
      <Route path="/admin/pro-alertas" element={<AdminProAlertas />} />
      <Route path="/admin/propiedades" element={<AdminPropiedades />} />
      <Route path="/admin/agente-whatsapp" element={<AdminAgenteWhatsApp />} />
      <Route path="/admin/agente-whatsapp/conversaciones" element={<AdminAgenteConversaciones />} />
      </Routes>
    </>
  );
}

export default App;
